/**
 * What this product does, and what it does not do yet.
 * @file src/web/shared/capabilities.ts
 *
 * ── One inventory, two renderings ─────────────────────────────────────────
 * The home page shows this as a flat grid — a reader scrolling past wants a
 * sense of scope in four seconds. /features groups it by the stage of intake
 * it belongs to, because someone who navigated there deliberately is looking
 * for a specific thing and needs somewhere to look.
 *
 * Same rows, two shapes. Kept in one file so a capability cannot ship on one
 * page and be missing from the other, and — the failure that actually costs
 * something — so `ready` cannot say true in one place and false in the other.
 *
 * ── `ready` is the whole point of this file ───────────────────────────────
 * Whatever is not built stays in the list with `ready: false` rather than
 * being quietly removed, because a firm that adopted this expecting a
 * capability would find out on its first enquiry, and finding out then is far
 * worse than reading "coming" here. The page renders the flag rather than
 * hiding the row, and the counts on both pages are derived from it — so this
 * file is the only place the honesty has to hold.
 *
 * @llm-rule WHEN: Adding or changing a capability shown on the site
 * @llm-rule AVOID: Setting ready: true before the thing actually works
 * @llm-rule AVOID: Deleting an unbuilt row to make the grid look fuller
 * @llm-rule AVOID: Naming a case-management system here that has no
 *   connector in src/api/features/cms — this row is checkable in an
 *   afternoon by anybody evaluating us
 */

import {
  BellRing,
  Bot,
  CalendarClock,
  ClipboardList,
  FileSignature,
  Gauge,
  Globe,
  History,
  KanbanSquare,
  LayoutTemplate,
  Mail,
  MailPlus,
  MessageSquare,
  PenLine,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Sparkles,
  TrendingDown,
  Users,
} from 'lucide-react';

/** The stages of intake, in the order they happen to a person. */
export const GROUPS = [
  {
    id: 'form',
    title: 'The form itself',
    lead: 'What a client actually meets, in the language they actually read.',
  },
  {
    id: 'lost',
    title: 'The people who never finished',
    lead: 'The half of intake most systems record as an absence rather than a person.',
  },
  {
    id: 'triage',
    title: 'Who to call first',
    lead: 'Ranking the queue, and checking you are allowed to act before you do.',
  },
  {
    id: 'signed',
    title: 'Getting signed',
    lead: 'From agreement text to a countersigned PDF with the audit trail attached.',
  },
  {
    id: 'onward',
    title: 'Where it goes next',
    lead: 'Handing a signed client to the system that will run their matter.',
  },
] as const;

export type GroupId = (typeof GROUPS)[number]['id'];

export interface Capability {
  icon: typeof Globe;
  name: string;
  detail: string;
  ready: boolean;
  group: GroupId;
}

export const CAPABILITIES: Capability[] = [
  /* ── the form ─────────────────────────────────────────────────────── */
  {
    icon: ClipboardList,
    name: 'Custom intake forms',
    detail: 'Nine question types, conditional logic, file upload.',
    ready: true,
    group: 'form',
  },
  {
    icon: Globe,
    name: 'True multi-language',
    detail: 'Your questions translated, not just our buttons. EN/ES/RU.',
    ready: true,
    group: 'form',
  },
  {
    icon: LayoutTemplate,
    name: 'Forms you do not have to write',
    detail: 'Forty intake forms across eleven practice areas — send one as it stands, or edit it.',
    ready: true,
    group: 'form',
  },
  {
    icon: RotateCcw,
    name: 'Resume where they stopped',
    detail: 'Answers saved as they type, and the link returns them to the question they left on.',
    ready: true,
    group: 'form',
  },

  /* ── the ones who did not finish ──────────────────────────────────── */
  {
    icon: TrendingDown,
    name: 'Drop-off detection',
    detail: 'Both stalls, told apart, each with its own follow-up.',
    ready: true,
    group: 'lost',
  },
  {
    icon: Mail,
    name: 'Automated follow-up',
    detail: 'Per-language nudges. Each person reached once, guaranteed.',
    ready: true,
    group: 'lost',
  },
  {
    icon: History,
    name: 'Visit history',
    detail: 'How many times they opened it and when — effort measured, not guessed from elapsed time.',
    ready: true,
    group: 'lost',
  },
  {
    icon: BellRing,
    name: 'Built-in chasing',
    detail: 'Five behaviours, each a sequence you can extend — nudge, then offer a call. Anyone who acts drops out of the rest on their own.',
    ready: true,
    group: 'lost',
  },

  {
    icon: MailPlus,
    name: 'Email templates',
    detail: 'Written once per language, with the details filled in as you send.',
    ready: true,
    group: 'lost',
  },
  {
    icon: BellRing,
    name: 'Alerts when it matters',
    detail: 'Six moments worth knowing about, by email. SMS, WhatsApp and push are next.',
    ready: true,
    group: 'lost',
  },

  /* ── triage ───────────────────────────────────────────────────────── */
  {
    icon: Gauge,
    name: 'Lead scoring',
    detail: 'Your rules, and every score shows which ones fired.',
    ready: true,
    group: 'triage',
  },
  {
    icon: ScanSearch,
    name: 'Conflict checking',
    detail: 'Every party on every matter, open and closed.',
    ready: true,
    group: 'triage',
  },
  {
    icon: Users,
    name: 'Round-robin assignment',
    detail: 'Next in the rotation, derived rather than counted.',
    ready: true,
    group: 'triage',
  },
  {
    icon: KanbanSquare,
    name: 'Pipeline stages you set',
    detail: 'Reorderable, and scoped to a matter type when one size does not fit.',
    ready: true,
    group: 'triage',
  },
  {
    icon: Bot,
    name: 'AI-led follow-up questions',
    detail: 'Asks what the story needs. Never answers, never advises.',
    ready: true,
    group: 'triage',
  },
  {
    icon: MessageSquare,
    name: 'Call and meeting notes',
    detail:
      'What they said on the phone, kept beside what they typed on the form — and searchable the same way.',
    ready: true,
    group: 'triage',
  },
  {
    icon: Sparkles,
    name: 'Ask your pipeline',
    detail:
      'Plain questions about your own intake, answered from your data — including what someone mentioned on a call.',
    ready: true,
    group: 'triage',
  },
  {
    icon: CalendarClock,
    name: 'Consultation scheduling',
    detail: 'A booking link clients use themselves, and an outcome on every appointment — went ahead, or did not attend.',
    ready: true,
    group: 'triage',
  },

  /* ── signing ──────────────────────────────────────────────────────── */
  {
    icon: FileSignature,
    name: 'E-signature',
    detail: 'Frozen document text, separate consent, full attribution.',
    ready: true,
    group: 'signed',
  },
  {
    icon: PenLine,
    name: 'Retainer templates',
    detail: 'Written once, per language, with the fee and the description filled in as you send.',
    ready: true,
    group: 'signed',
  },

  /* ── onward ───────────────────────────────────────────────────────── */
  {
    icon: RefreshCw,
    name: 'Opens the matter in your case system',
    /* What this actually does: the firm connects its own Clio or Filevine,
     * and converting a lead creates the contact, opens the matter there and
     * files the signed retainer and the client's uploads against it. It
     * reads nothing back, so this must not say "sync" or "two-way" — a firm
     * told that will assume a change made in Clio comes back here, and it
     * does not. No other case system is named, because no other one is
     * built. */
    detail: 'Connect Clio or Filevine and a converted lead opens the matter there — client attached, signed retainer and uploads filed with it.',
    ready: true,
    group: 'onward',
  },
];

/**
 * The chasing rules, and which of them actually run today.
 *
 * All five now do. The acknowledgement is the odd one out: it is sent by the
 * submission path the moment a prospect first has an address, not by the
 * hourly sweep, because an acknowledgement that arrives an hour late has
 * missed the only moment it was for.
 */
export const BEHAVIOURS = [
  {
    when: 'A new enquiry arrives',
    then: 'Acknowledge it in their language, the moment we have an address for them.',
    live: true,
  },
  {
    when: 'A form sits half-finished for 30 minutes',
    then: 'Send the link back to where they stopped — however they reached the form.',
    live: true,
  },
  {
    when: 'They finished the form but never signed',
    then: 'Explain the retainer in plain words. Then offer a call.',
    live: true,
  },
  {
    when: 'They signed but never finished the form',
    then: 'Send the questionnaire back, resumable. Then offer to do it by phone.',
    live: true,
  },
  {
    when: 'They signed',
    then: 'Tell them what happens next and what to gather, while the firm catches up.',
    live: true,
  },
];

export const shippedCount = CAPABILITIES.filter((c) => c.ready).length;
export const liveBehaviourCount = BEHAVIOURS.filter((b) => b.live).length;
export const inGroup = (id: GroupId) => CAPABILITIES.filter((c) => c.group === id);
